const TURBO_ENTITLEMENTS = Object.freeze({
  maxStations: 3,
  playlistGeneration: true,
  automatedSync: true,
  prioritySupport: false,
  earlyAccess: false,
});

export const PLANS = Object.freeze({
  starter: Object.freeze({
    tier: 'starter',
    name: 'Starter',
    priceUsd: 49,
    maxStations: 1,
    playlistGeneration: true,
    automatedSync: false,
    prioritySupport: false,
    earlyAccess: false,
    comparePlan: null,
  }),
  turbo: Object.freeze({
    tier: 'turbo',
    name: 'Turbo',
    priceUsd: 99,
    ...TURBO_ENTITLEMENTS,
    comparePlan: null,
  }),
  max: Object.freeze({
    tier: 'max',
    name: 'Max',
    priceUsd: 149,
    maxStations: null,
    playlistGeneration: true,
    automatedSync: true,
    prioritySupport: true,
    earlyAccess: true,
    comparePlan: null,
  }),
  founding_partner: Object.freeze({
    tier: 'founding_partner',
    name: 'Founding Partner',
    priceUsd: 39,
    ...TURBO_ENTITLEMENTS,
    comparePlan: 'turbo',
  }),
});

export const PUBLIC_PLAN_TIERS = Object.freeze(['starter', 'turbo', 'max']);

export const FOUNDING_PARTNER_ORG_IDS = Object.freeze(['org_3EsQgZl5tWLpRup5LLbzbqMgtu1']);

const isKnownTier = (tier) => typeof tier === 'string' && Object.hasOwn(PLANS, tier);

export const resolvePlan = (tier) => (isKnownTier(tier) ? PLANS[tier] : PLANS.starter);

export const planFromServer = (payload) =>
  payload?.plan && 'tier' in payload.plan ? resolvePlan(payload.plan.tier) : null;

export const canLinkStation = (plan, linkedCount) =>
  plan.maxStations === null || linkedCount < plan.maxStations;

export const hasAutomatedSync = (plan) => plan.automatedSync;

const stationCapacity = (plan) => plan.maxStations ?? Infinity;

export const upgradePlanFor = (plan, need) => {
  const satisfies = need === 'automatedSync'
    ? (candidate) => candidate.automatedSync
    : (candidate) => stationCapacity(candidate) > stationCapacity(plan);
  const tier = PUBLIC_PLAN_TIERS.find(t => PLANS[t].priceUsd > plan.priceUsd && satisfies(PLANS[t]));
  return tier ? PLANS[tier] : null;
};

export const formatPlanPrice = (plan) => `$${plan.priceUsd}/mo`;

export const stationAllowanceLabel = (plan) => {
  if (plan.maxStations === null) return 'Unlimited Media Stations';
  if (plan.maxStations === 1) return '1 Media Station';
  return `Up to ${plan.maxStations} Media Stations`;
};

const lowerFirst = (text) => text.charAt(0).toLowerCase() + text.slice(1);

export const stationLimitMessage = (plan) => {
  const next = upgradePlanFor(plan, 'stations');
  const base = `Your ${plan.name} plan includes ${lowerFirst(stationAllowanceLabel(plan))}.`;
  if (!next) return base;
  return `${base} Upgrade to ${next.name} (${formatPlanPrice(next)}) to link ${lowerFirst(stationAllowanceLabel(next))}.`;
};

export const automatedSyncMessage = (plan) => {
  const next = upgradePlanFor(plan, 'automatedSync');
  return `Auto-sync is not included in your ${plan.name} plan. Upgrade to ${next.name} (${formatPlanPrice(next)}) to turn it on.`;
};

const coversOrg = (plan, stations) => stations <= stationCapacity(plan) && plan.automatedSync;

export const existingOrgTier = ({ orgId, tier, stations }) => {
  if (FOUNDING_PARTNER_ORG_IDS.includes(orgId)) return 'founding_partner';
  if (isKnownTier(tier) && coversOrg(PLANS[tier], stations)) return tier;
  return stations > PLANS.turbo.maxStations ? 'max' : 'turbo';
};

export const planDryRun = (orgs, { assign = existingOrgTier } = {}) =>
  orgs.map(({ org_id: orgId, org_name: orgName, subscription_tier: currentTier, stations, auto_on_users: autoOnUsers }) => {
    const tier = assign({ orgId, tier: currentTier, stations });
    const plan = resolvePlan(tier);
    const withinLimit = stations <= stationCapacity(plan);
    const flags = [];
    if (tier === 'founding_partner') flags.push('founding_partner');
    if (stations > PLANS.turbo.maxStations && tier === 'max' && currentTier !== 'max') flags.push('mapped_up_for_stations');
    if (tier === 'turbo' && currentTier !== 'turbo' && stations <= PLANS.starter.maxStations) {
      flags.push('grandfathered_automated_sync');
    }
    return {
      orgId,
      orgName,
      currentTier,
      tier,
      priceUsd: plan.priceUsd,
      stations,
      maxStations: plan.maxStations,
      autoOnUsers,
      automatedSync: plan.automatedSync,
      withinLimit,
      losesAccess: !withinLimit || !plan.automatedSync,
      flags,
    };
  });

const GENERIC_STATION_LIMIT_MESSAGE =
  'You have reached the Media Station limit for your plan. Upgrade your plan to link more Media Stations.';

export const linkStationGate = (plan, linkedCount) =>
  !plan || canLinkStation(plan, linkedCount)
    ? { allowed: true, message: null }
    : { allowed: false, message: stationLimitMessage(plan) };

export const autoSyncGate = (plan, currentlyEnabled) =>
  !plan || currentlyEnabled || hasAutomatedSync(plan)
    ? { canTurnOn: true, message: null }
    : { canTurnOn: false, message: automatedSyncMessage(plan) };

export const planErrorMessage = (error, plan) => {
  if (error?.code === 'station_limit_reached') {
    return plan ? stationLimitMessage(plan) : GENERIC_STATION_LIMIT_MESSAGE;
  }
  if (error?.code === 'automated_sync_not_in_plan') {
    return plan
      ? automatedSyncMessage(plan)
      : `Auto-sync is not included in your plan. Upgrade to ${PLANS.turbo.name} (${formatPlanPrice(PLANS.turbo)}) to turn it on.`;
  }
  return null;
};

export const planSummary = (plan) => {
  const compare = plan.comparePlan ? PLANS[plan.comparePlan] : null;
  return {
    name: plan.name,
    price: formatPlanPrice(plan),
    comparison: compare ? `${compare.name} plan, normally ${formatPlanPrice(compare)}` : null,
    features: [
      stationAllowanceLabel(plan),
      ...(plan.playlistGeneration ? ['Playlist generation'] : []),
      plan.automatedSync ? 'Automated sync' : 'No automated sync',
      ...(plan.prioritySupport ? ['Priority support'] : []),
      ...(plan.earlyAccess ? ['Early access to new features and releases'] : []),
    ],
  };
};
